/**
 * Hook useKanbanConversationCrmData - Busca dados CRM em batch para cards do Kanban de Conversas
 * 
 * Chama a RPC get_kanban_conversation_crm_data para obter deal + contrato
 * por lista de contact_ids (1 chamada por coluna, não 1 por card)
 * 
 * Versão: 1.0.0 - 2026-03-07
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger condicional
const logger = createLogger('KanbanConversationCrmData');

/**
 * Interface dos dados CRM retornados por contato
 */
export interface ContactCrmData {
  contact_id: string;
  deal_id: string;
  deal_title: string;
  deal_value: number;
  deal_status: string; // 'open' | 'won' | 'lost'
  deal_pipeline_name: string | null;
  deal_phase_name: string | null;
  deal_phase_color: string | null;
  extra_open_deals_count: number;
  last_contract_status: string | null;
  last_contract_title: string | null;
}

/**
 * Hook para buscar dados CRM em batch por lista de contact_ids
 * 
 * @param contactIds - Array de IDs de contatos (extraídos das conversas da coluna)
 * @param enabled - Se deve buscar os dados (combina crm_enabled + showCrmData + ter IDs)
 * @returns Map<contact_id, ContactCrmData> para lookup rápido no KanbanCard
 */
export function useKanbanConversationCrmData(
  contactIds: string[],
  enabled: boolean = true
) {
  const { currentTenant } = useAuth();

  const query = useQuery({
    // Chave inclui IDs ordenados para cache estável
    queryKey: ['kanban-conversation-crm-data', contactIds.sort().join(',')],
    queryFn: async () => {
      logger.debug(`Buscando dados CRM para ${contactIds.length} contatos`);

      // Chamada RPC com cast para unknown (função não tipada no types.ts)
      const { data, error } = await (supabase.rpc as any)('get_kanban_conversation_crm_data', {
        p_contact_ids: contactIds,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao buscar dados CRM do Kanban:', error);
        throw error;
      }

      // Converte array em Map para lookup O(1) no KanbanCard
      const result = (data || []) as unknown as ContactCrmData[];
      const map = new Map<string, ContactCrmData>();
      result.forEach(item => {
        map.set(item.contact_id, item);
      });

      logger.debug(`Dados CRM carregados: ${map.size} contatos com deals`);
      return map;
    },
    enabled: enabled && contactIds.length > 0 && !!currentTenant?.id,
    staleTime: 60 * 1000, // 60 segundos (dados CRM mudam menos que mensagens)
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    crmDataMap: query.data || new Map<string, ContactCrmData>(),
    isLoading: query.isLoading,
    error: query.error,
  };
}
